import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "modal", "searchStep", "timeStep", "searchInput",
        "selectedProductDisplay", "confirmButton", "searchContainer", "modalSearchContainer",
        "batchStep", "batchProducts", "batchConfirmButton", "batchError",
        "initialProduct", "controls", "introHeading", "scanButton", "scanLabel", "scanHint", "orDivider"
    ]
    static values = {
        routineId: Number,
        productsPath: String,
        batchProductsPath: String,
        keepExpanded: { type: Boolean, default: false },
        searchPlaceholder: String,
        compact: { type: Boolean, default: false },
        addToOwnedShelf: { type: Boolean, default: true }
    }

    connect() {
        this.currentProduct = null
        this.scannedProducts = []
        this.modalTimeOfDay = 1
        this.searchTimeout = null

        if (this.hasInitialProductTarget) {
            this.moveSearchInputToModal()
            this.currentProduct = { id: this.initialProductTarget.dataset.productId }
            this.selectedProductDisplayTarget.replaceChildren(this.initialProductTarget.content.cloneNode(true))
            this.modalTarget.style.display = 'flex'
            this.showTimeStep()
        }
    }

    disconnect() {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
    }

    // Signal that the user engaged with this step. Tapping the scanner counts
    // as much as opening search: both leave people who back out without adding
    // anything with a usable step action.
    markStepEngaged() {
        document.dispatchEvent(new CustomEvent('onboarding:product-search-tapped', {
            bubbles: true,
            detail: { tapped: true }
        }))
    }

    // Collapse the scan and search controls once the routine has something in
    // it, so the routine list owns the top of the screen instead of the pitch.
    showCompactControls() {
        if (this.compactValue || this.keepExpandedValue) return

        this.compactValue = true

        if (this.hasControlsTarget) {
            this.controlsTarget.classList.remove("mt-6")
        }

        if (this.hasIntroHeadingTarget) this.introHeadingTarget.classList.add("hidden")
        if (this.hasScanHintTarget) this.scanHintTarget.classList.add("hidden")
        if (this.hasOrDividerTarget) this.orDividerTarget.classList.add("hidden")
        if (this.hasScanLabelTarget) this.scanLabelTarget.classList.add("hidden")

        if (this.hasScanButtonTarget) {
            this.scanButtonTarget.classList.remove("h-14", "w-full", "px-5", "bg-indigo-900", "text-white")
            this.scanButtonTarget.classList.add("absolute", "right-1.5", "top-1.5", "z-10", "size-11", "text-warm-gray-500")
        }

        if (this.hasSearchInputTarget && this.searchContainerTarget?.contains(this.searchInputTarget)) {
            this.searchInputTarget.placeholder = this.outsideSearchPlaceholder
            if (this.hasScanButtonTarget) this.searchInputTarget.classList.add("pr-10")
        }
    }

    get outsideSearchPlaceholder() {
        return this.searchPlaceholderValue || (this.compactValue ? "Add another product" : "Search by name or brand")
    }

    // Open the modal
    openModal() {
        this.markStepEngaged()

        // Move the search input to the modal
        this.moveSearchInputToModal()

        this.modalTarget.style.display = 'flex'
        this.showSearchStep()

        // The input should already be focused since it triggered the modal opening
        // But let's ensure it stays focused
        setTimeout(() => {
            if (this.hasSearchInputTarget) {
                this.searchInputTarget.focus()
            }
        }, 100)
    }

    // Close the modal
    closeModal() {
        this.modalTarget.style.display = 'none'

        // Move the search input back to outside the modal
        this.moveSearchInputToOutside()

        this.resetModal()
    }

    // Move search input to modal container
    moveSearchInputToModal() {
        if (this.hasSearchContainerTarget && this.hasModalSearchContainerTarget) {
            const searchForm = this.searchContainerTarget.firstElementChild
            if (searchForm) {
                // Update placeholder text for modal context
                const searchInput = searchForm.querySelector('input[name="search[term]"]')
                if (searchInput) {
                    searchInput.placeholder = this.searchPlaceholderValue || "Search for products to add"
                    searchInput.classList.remove("pr-10")
                }

                this.modalSearchContainerTarget.appendChild(searchForm)
            }
        }
    }

    // Move search input back to outside container
    moveSearchInputToOutside() {
        if (this.hasSearchContainerTarget && this.hasModalSearchContainerTarget) {
            const searchForm = this.modalSearchContainerTarget.firstElementChild
            if (searchForm) {
                // Update placeholder text for outside context
                const searchInput = searchForm.querySelector('input[name="search[term]"]')
                if (searchInput) {
                    searchInput.placeholder = this.outsideSearchPlaceholder
                    searchInput.classList.toggle("pr-10", this.compactValue && this.hasScanButtonTarget)
                }

                this.searchContainerTarget.appendChild(searchForm)
            }
        }
    }

    // Show search step
    showSearchStep() {
        this.searchStepTarget.style.display = 'flex'
        this.timeStepTarget.style.display = 'none'
        if (this.hasBatchStepTarget) this.batchStepTarget.style.display = 'none'
    }

    // Show time selection step
    showTimeStep() {
        this.searchStepTarget.style.display = 'none'
        this.timeStepTarget.style.display = 'flex'
        if (this.hasBatchStepTarget) this.batchStepTarget.style.display = 'none'
    }

    showBatchStep() {
        this.searchStepTarget.style.display = 'none'
        this.timeStepTarget.style.display = 'none'
        this.batchStepTarget.style.display = 'flex'
    }

    // Go back to search from time selection
    goBackToSearch() {
        this.showSearchStep()
        this.currentProduct = null
    }

    // Reset modal to initial state
    resetModal() {
        this.showSearchStep()
        this.currentProduct = null
        this.scannedProducts = []
        this.modalTimeOfDay = 1

        if (this.hasBatchProductsTarget) this.batchProductsTarget.replaceChildren()
        this.hideBatchError()

        // Clear search input
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ''
        }

        // Reset radio buttons
        const radioButtons = this.element.querySelectorAll('input[name="modal_time_of_day"]')
        radioButtons.forEach((radio, index) => {
            radio.checked = index === 0 // Check first one (Morning)
        })
    }

    // Handle search input with debouncing
    handleSearchInput(event) {
        // Clear existing timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }

        const searchTerm = event.target.value.trim()

        // If search is empty, don't search
        if (searchTerm.length === 0) {
            return
        }

        // Set a new timeout for debounced search
        this.searchTimeout = setTimeout(() => {
            this.performSearch(searchTerm)
        }, 300) // 300ms debounce
    }

    // Perform the actual search
    performSearch(searchTerm) {
        const form = document.getElementById('onboarding-search-form')
        if (!form) return

        // Submit the form to trigger turbo frame update
        const formData = new FormData(form)
        const url = new URL(form.action)

        // Add form data to URL as query parameters
        for (const [key, value] of formData.entries()) {
            url.searchParams.set(key, value)
        }

        // Use the trimmed search term for the URL (without modifying the input)
        url.searchParams.set('search[term]', searchTerm)

        // Update the turbo frame
        const turboFrame = document.getElementById('onboarding_product_search_results')
        if (turboFrame) {
            turboFrame.src = url.toString()
        }
    }

    // Add product to selection (called from search results)
    addProduct(event) {
        const button = event.currentTarget
        const productId = button.dataset.productId
        const productHtml = button.innerHTML

        // Store the selected product
        this.currentProduct = {
            id: productId,
            html: productHtml
        }

        // Update the selected product display
        this.selectedProductDisplayTarget.innerHTML = productHtml

        // Move to time selection step
        this.showTimeStep()
    }

    addScannedProducts(event) {
        const products = Array.from(event.detail?.products || [])
            .map(product => this.normalizeScannedProduct(product))
            .filter(product => product.id)

        this.scannedProducts = Array.from(
            new Map(products.map(product => [product.id, product])).values()
        )

        if (this.scannedProducts.length === 0) return

        document.dispatchEvent(new CustomEvent('onboarding:product-search-tapped', {
            bubbles: true,
            detail: { source: 'scanner' }
        }))

        this.renderScannedProducts()
        this.modalTarget.style.display = 'flex'
        this.showBatchStep()
    }

    normalizeScannedProduct(product) {
        return {
            id: Number(product.product_id || product.productId),
            brandName: product.brand_name || product.brandName || '',
            productName: product.product_name || product.productName || '',
            imageUrl: product.image_url || product.imageUrl || '',
            morning: true,
            evening: true
        }
    }

    renderScannedProducts() {
        this.batchProductsTarget.replaceChildren(
            ...this.scannedProducts.map(product => this.scannedProductRow(product))
        )
    }

    scannedProductRow(product) {
        const row = document.createElement('div')
        row.className = 'rounded-2xl bg-white p-3 ring-1 ring-warm-gray-200'
        row.dataset.productId = product.id

        const details = document.createElement('div')
        details.className = 'flex items-center gap-3'

        const image = document.createElement('img')
        image.src = product.imageUrl
        image.alt = ''
        image.className = 'size-14 shrink-0 rounded-xl bg-warm-gray-50 object-contain'

        const copy = document.createElement('div')
        copy.className = 'min-w-0 flex-1'

        const name = document.createElement('div')
        name.className = 'truncate text-sm font-medium text-warm-gray-900'
        name.textContent = product.productName

        const brand = document.createElement('div')
        brand.className = 'mt-0.5 truncate text-xs text-warm-gray-500'
        brand.textContent = product.brandName

        copy.append(name, brand)
        details.append(image, copy)

        const choices = document.createElement('div')
        choices.className = 'mt-3 grid grid-cols-2 gap-2'
        choices.append(
            this.scannedProductTimeButton(product, 'morning', 'AM'),
            this.scannedProductTimeButton(product, 'evening', 'PM')
        )

        row.append(details, choices)
        return row
    }

    scannedProductTimeButton(product, period, title) {
        const button = document.createElement('button')
        button.type = 'button'
        button.dataset.productId = product.id
        button.dataset.period = period
        button.dataset.action = 'click->onboarding-product-selection#toggleScannedProductTime'

        const check = document.createElement('span')
        check.className = 'scanned-time-check'
        check.innerHTML = '<svg class="size-4" fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path clip-rule="evenodd" fill-rule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"></path></svg>'

        const label = document.createElement('span')
        label.textContent = title

        button.append(check, label)
        this.updateScannedProductTimeButton(button, product[period])
        return button
    }

    toggleScannedProductTime(event) {
        const button = event.currentTarget
        const product = this.scannedProducts.find(candidate => candidate.id === Number(button.dataset.productId))
        if (!product) return

        product[button.dataset.period] = !product[button.dataset.period]
        this.updateScannedProductTimeButton(button, product[button.dataset.period])
        this.hideBatchError()
    }

    updateScannedProductTimeButton(button, selected) {
        const selectedClasses = button.dataset.period === 'morning'
            ? 'flex items-center justify-center gap-1.5 rounded-xl bg-amber-100 px-3 py-2.5 text-sm font-semibold text-amber-900 ring-2 ring-inset ring-amber-500'
            : 'flex items-center justify-center gap-1.5 rounded-xl bg-violet-100 px-3 py-2.5 text-sm font-semibold text-violet-900 ring-2 ring-inset ring-violet-500'

        button.className = selected
            ? selectedClasses
            : 'flex items-center justify-center gap-1.5 rounded-xl bg-white px-3 py-2.5 text-sm font-semibold text-warm-gray-500 ring-1 ring-inset ring-warm-gray-300'

        const check = button.querySelector('.scanned-time-check')
        if (check) check.classList.toggle('hidden', !selected)

        button.setAttribute('aria-pressed', selected ? 'true' : 'false')
    }

    async confirmScannedProducts() {
        const products = this.scannedProducts.map(product => ({
            product_id: product.id,
            time_of_day: this.scannedProductTimeOfDay(product)
        }))

        if (products.some(product => product.time_of_day === null)) {
            this.showBatchError('Choose AM, PM, or both for every product.')
            return
        }

        const button = this.batchConfirmButtonTarget
        button.disabled = true
        this.application
            .getControllerForElementAndIdentifier(button, 'loading-button')
            ?.showLoading()

        try {
            const response = await fetch(this.batchProductsPathValue, {
                method: 'POST',
                headers: {
                    'Accept': 'text/vnd.turbo-stream.html',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.getCSRFToken(),
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    scan_session: {
                        routine_id: this.routineIdValue,
                        products
                    }
                })
            })

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}))
                this.showBatchError(payload.message || 'We couldn’t add these products. Please try again.')
                return
            }

            const responseText = await response.text()
            window.Turbo?.renderStreamMessage(responseText)
            this.showCompactControls()

            products.forEach(product => {
                document.dispatchEvent(new CustomEvent('onboarding:product-added', {
                    bubbles: true,
                    detail: { productId: product.product_id, source: 'scanner' }
                }))
            })

            this.closeModal()
        } catch (error) {
            console.error('Error adding scanned products:', error)
            this.showBatchError('We couldn’t add these products. Please try again.')
        } finally {
            button.disabled = false
            button.dispatchEvent(new CustomEvent('loading-button:stop'))
        }
    }

    scannedProductTimeOfDay(product) {
        if (product.morning && product.evening) return 3
        if (product.morning) return 1
        if (product.evening) return 2
        return null
    }

    showBatchError(message) {
        if (!this.hasBatchErrorTarget) return

        this.batchErrorTarget.textContent = message
        this.batchErrorTarget.classList.remove('hidden')
    }

    hideBatchError() {
        if (!this.hasBatchErrorTarget) return

        this.batchErrorTarget.textContent = ''
        this.batchErrorTarget.classList.add('hidden')
    }

    // Update modal time of day selection
    updateModalTimeOfDay(event) {
        this.modalTimeOfDay = parseInt(event.target.value)
    }

    // Confirm and add product to routine
    async confirmAddProduct() {
        if (!this.currentProduct) return

        const confirmButton = this.confirmButtonTarget
        this.updateConfirmButton(confirmButton, true)

        try {
            const formData = new FormData()
            formData.append('routine_product[routine_id]', this.routineIdValue)
            formData.append('routine_product[product_id]', this.currentProduct.id)
            formData.append('routine_product[time_of_day]', this.modalTimeOfDay)
            formData.append('routine_product[add_to_owned_shelf]', this.addToOwnedShelfValue ? '1' : '0')
            formData.append('authenticity_token', this.getCSRFToken())

            const response = await fetch(this.productsPathValue, {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'text/vnd.turbo-stream.html, text/html',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (response.ok) {
                const responseText = await response.text()

                // Let Turbo handle the stream response
                if (response.headers.get('content-type')?.includes('turbo-stream')) {
                    window.Turbo?.renderStreamMessage(responseText)
                }

                this.showCompactControls()

                document.dispatchEvent(new CustomEvent('onboarding:product-added', {
                    bubbles: true,
                    detail: { productId: this.currentProduct.id }
                }))

                // Close modal
                this.closeModal()

            } else {
                console.error('Failed to add product', response.status, response.statusText)
            }
        } catch (error) {
            console.error('Error adding product:', error)
        } finally {
            this.updateConfirmButton(confirmButton, false)
            confirmButton.dispatchEvent(new CustomEvent('loading-button:stop'))
        }
    }

    // Update confirm button state
    updateConfirmButton(button, isLoading) {
        const addText = button.querySelector('.add-text')
        const addingText = button.querySelector('.adding-text')

        if (isLoading) {
            button.disabled = true
            addText.classList.add('hidden')
            addingText.classList.remove('hidden')
        } else {
            button.disabled = false
            addText.classList.remove('hidden')
            addingText.classList.add('hidden')
        }
    }

    // Helper methods
    getCSRFToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || ''
    }

}
